import React, { useState, useEffect } from "react";
import { 
  FiChevronDown, 
  FiChevronUp, 
  FiMail, 
  FiPhone, 
  FiCalendar, 
  FiCheckCircle, 
  FiClock, 
  FiXCircle,
  FiTrendingUp,
  FiAward,
  FiActivity,
  FiStar,
  FiEdit3
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
//import requestImg from "../components/assets/quote-request.png";
import userImg from "../components/assets/user.png";

const UserDetails = ({ 
  user, 
  request_amount, 
  approvedOrders = [], 
  rejectedOrders = [], 
  pendingOrders = [] 
}) => {
  const [expandedSections, setExpandedSections] = useState({
    approved: false,
    pending: false,
    rejected: false
  });
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({});
  const [stats, setStats] = useState({
    totalRequests: 0,
    approvalRate: 0,
    avgProcessingTime: 0,
    completionRate: 0,
    monthlyTrend: "up"
  });

  useEffect(() => {
    const total = approvedOrders.length + rejectedOrders.length + pendingOrders.length;
    const rate = total > 0 ? Math.round((approvedOrders.length / total) * 100) : 0;
    const completionRate = total > 0 ? Math.round(((approvedOrders.length + rejectedOrders.length) / total) * 100) : 0;
    
    setStats({
      totalRequests: total,
      approvalRate: rate,
      avgProcessingTime: calculateAvgProcessingTime(),
      completionRate,
      monthlyTrend: calculateMonthlyTrend()
    });

    setEditedUser(user || {});
  }, [approvedOrders, rejectedOrders, pendingOrders, user]);

  const calculateAvgProcessingTime = () => {
    const processedOrders = [...approvedOrders, ...rejectedOrders];
    if (processedOrders.length === 0) return 0;
    
    const totalDays = processedOrders.reduce((sum, order) => {
      if (!order.createdAt || !order.updatedAt) return sum;
      const created = new Date(order.createdAt);
      const updated = new Date(order.updatedAt);
      return sum + Math.ceil((updated - created) / (1000 * 60 * 60 * 24));
    }, 0);
    
    return Math.round(totalDays / processedOrders.length);
  };

  const calculateMonthlyTrend = () => {
    // Simple trend calculation based on recent activity
    const recentOrders = [...approvedOrders, ...rejectedOrders, ...pendingOrders]
      .filter(order => {
        const orderDate = new Date(order.createdAt);
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return orderDate > monthAgo;
      });
    
    return recentOrders.length > stats.totalRequests / 3 ? "up" : "down";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

 /* const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };*/

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const names = name.split(" ");
    return names.map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  /*const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "from-green-500 to-green-600";
      case "pending": return "from-yellow-500 to-yellow-600";
      case "rejected": return "from-red-500 to-red-600";
      default: return "from-gray-500 to-gray-600";
    }
  };*/

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-800";
      case "medium": return "bg-yellow-100 text-yellow-800";
      case "low": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const statusConfig = {
    approved: {
      color: "from-green-500 to-green-600",
      icon: <FiCheckCircle className="text-xl" />,
      title: "Approved Requests",
      description: "Successfully approved requests"
    },
    pending: {
      color: "from-yellow-500 to-yellow-600",
      icon: <FiClock className="text-xl" />,
      title: "Pending Requests",
      description: "Requests awaiting review"
    },
    rejected: {
      color: "from-red-500 to-red-600",
      icon: <FiXCircle className="text-xl" />,
      title: "Rejected Requests",
      description: "Requests that were not approved"
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      // Save logic would go here
      console.log("Saving user data:", editedUser);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderOrderList = (orders, type) => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ 
        opacity: expandedSections[type] ? 1 : 0,
        height: expandedSections[type] ? "auto" : 0
      }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <div className="mt-3 bg-gray-50 rounded-lg p-4 max-h-80 overflow-y-auto">
        {orders.length > 0 ? (
          <div className="grid gap-3">
            {orders.map((req, index) => (
              <motion.div
                key={`${type}-${index}`}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-800">#{req.orderNumber}</span>
                      {req.priority && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(req.priority)}`}>
                          {req.priority}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{req.Title || req.description || "No description"}</p>
                  </div>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {formatDate(req.createdAt)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>Amount: ${req.totalAmount || req.amount || "N/A"}</span>
                  <span>Items: {req.products?.length || req.items?.length || 0}</span>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">📭</div>
            <p className="text-gray-500">No {type} requests found</p>
          </div>
        )}
      </div>
    </motion.div>
  );

  const StatCard = ({ icon, label, value, trend, color }) => (
    <motion.div 
      whileHover={{ y: -2, scale: 1.02 }}
      className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
    >
      <div className="flex justify-between items-start">
        <div>
          <div className={`p-3 rounded-lg ${color} bg-opacity-10 inline-block mb-3`}>
            {icon}
          </div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        {trend && (
          <div className={`flex items-center ${trend === "up" ? "text-green-500" : "text-red-500"}`}>
            <FiTrendingUp className={trend === "down" ? "transform rotate-180" : ""} />
          </div>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* User Profile Card */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="xl:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-8">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-32 relative">
                <button 
                  onClick={handleEditToggle}
                  className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 p-2 rounded-full transition-all"
                >
                  <FiEdit3 className="text-white" />
                </button>
              </div>
              
              <div className="px-6 pb-8 relative">
                <div className="flex justify-center -mt-16 mb-4">
                  <div className="relative">
                    {user?.imageurl ? (
                      <img 
                        src={userImg} 
                        alt={user.name || "User"} 
                        className="w-32 h-32 rounded-full border-4 border-white object-cover shadow-lg"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-full border-4 border-white bg-gradient-to-br from-blue-100 to-purple-100 shadow-lg flex items-center justify-center">
                        <span className="text-3xl font-bold text-blue-600">
                          {getInitials(user?.name)}
                        </span>
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                </div>
                
                <div className="text-center mb-6">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedUser.name || ""}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      className="text-2xl font-bold text-gray-800 bg-gray-100 rounded px-3 py-1 text-center w-full mb-2"
                    />
                  ) : (
                    <h2 className="text-2xl font-bold text-gray-800">{user?.name || "Unknown User"}</h2>
                  )}
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedUser.role || ""}
                      onChange={(e) => handleInputChange("role", e.target.value)}
                      className="text-blue-600 font-medium bg-gray-100 rounded px-3 py-1 text-center w-full"
                    />
                  ) : (
                    <p className="text-blue-600 font-medium">{user?.role || "No role specified"}</p>
                  )}
                  
                  <div className="flex justify-center mt-2">
                    <div className="flex items-center bg-yellow-50 px-3 py-1 rounded-full">
                      <FiStar className="text-yellow-500 mr-1" />
                      <span className="text-sm font-medium text-yellow-700">Premium User</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <FiMail className="text-gray-500 text-lg" />
                    {isEditing ? (
                      <input
                        type="email"
                        value={editedUser.email || ""}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className="text-gray-700 bg-white rounded px-2 py-1 flex-1"
                      />
                    ) : (
                      <span className="text-gray-700">{user?.email || "No email provided"}</span>
                    )}
                  </div>
                  
                  {user?.phone && (
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <FiPhone className="text-gray-500 text-lg" />
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editedUser.phone || ""}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          className="text-gray-700 bg-white rounded px-2 py-1 flex-1"
                        />
                      ) : (
                        <span className="text-gray-700">{user.phone}</span>
                      )}
                    </div>
                  )}
                  
                  {user?.createdAt && (
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <FiCalendar className="text-gray-500 text-lg" />
                      <span className="text-gray-700">
                        Joined: {formatDate(user.createdAt.split("T")[0])}
                      </span>
                    </div>
                  )}
                </div>

                {/* Performance Badges */}
                <div className="mt-6">
                  <h3 className="font-semibold text-gray-700 mb-3">Achievements</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-blue-50 rounded-lg p-2 text-center">
                      <FiAward className="text-blue-500 mx-auto mb-1" />
                      <span className="text-xs text-blue-700">Top Requester</span>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 text-center">
                      <FiCheckCircle className="text-green-500 mx-auto mb-1" />
                      <span className="text-xs text-green-700">Fast Approver</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Main Content */}
          <div className="xl:col-span-3 space-y-8">
            {/* Header Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl p-8 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-3xl font-bold mb-2">User Dashboard</h1>
                    <p className="text-blue-100">Welcome back! Here's your request overview</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">${request_amount?.toLocaleString() || "0"}</p>
                    <p className="text-blue-100">Total Request Value</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Statistics Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              <StatCard
                icon={<FiActivity className="text-blue-500 text-xl" />}
                label="Total Requests"
                value={stats.totalRequests}
                trend={stats.monthlyTrend}
                color="text-blue-500"
              />
              <StatCard
                icon={<FiTrendingUp className="text-green-500 text-xl" />}
                label="Approval Rate"
                value={`${stats.approvalRate}%`}
                color="text-green-500"
              />
              <StatCard
                icon={<FiClock className="text-purple-500 text-xl" />}
                label="Avg. Processing"
                value={`${stats.avgProcessingTime}d`}
                color="text-purple-500"
              />
              <StatCard
                icon={<FiCheckCircle className="text-orange-500 text-xl" />}
                label="Completion Rate"
                value={`${stats.completionRate}%`}
                color="text-orange-500"
              />
            </motion.div>

            {/* Request Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="p-8">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-800">Request Analytics</h2>
                  <div className="flex space-x-2">
                    {["overview", "details", "timeline"].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          activeTab === tab
                            ? "bg-blue-100 text-blue-600"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {activeTab === "overview" && (
                    <motion.div
                      key="overview"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-6"
                    >
                      {/* Request Status Sections */}
                      {Object.entries(statusConfig).map(([type, config]) => {
                        const orders = type === "approved" ? approvedOrders : 
                                       type === "pending" ? pendingOrders : rejectedOrders;
                        return (
                          <div key={type} className="mb-6 last:mb-0">
                            <motion.button
                              whileTap={{ scale: 0.98 }}
                              className={`w-full bg-gradient-to-r ${config.color} rounded-xl p-6 shadow-lg text-white flex justify-between items-center hover:shadow-xl transition-shadow`}
                              onClick={() => toggleSection(type)}
                            >
                              <div className="flex items-center space-x-4">
                                <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                                  {config.icon}
                                </div>
                                <div className="text-left">
                                  <div className="text-2xl font-bold">{orders.length}</div>
                                  <div className="text-blue-100">{config.title}</div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-blue-100">{config.description}</span>
                                {expandedSections[type] ? (
                                  <FiChevronUp className="text-xl" />
                                ) : (
                                  <FiChevronDown className="text-xl" />
                                )}
                              </div>
                            </motion.button>
                            
                            {renderOrderList(orders, type)}
                          </div>
                        );
                      })}
                    </motion.div>
                  )}

                  {activeTab === "details" && (
                    <motion.div
                      key="details"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center py-12"
                    >
                      <div className="text-6xl mb-4">📊</div>
                      <h3 className="text-xl font-semibold text-gray-700">Detailed Analytics</h3>
                      <p className="text-gray-500">Coming soon - advanced analytics and insights</p>
                    </motion.div>
                  )}

                  {activeTab === "timeline" && (
                    <motion.div
                      key="timeline"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center py-12"
                    >
                      <div className="text-6xl mb-4">⏰</div>
                      <h3 className="text-xl font-semibold text-gray-700">Request Timeline</h3>
                      <p className="text-gray-500">Visual timeline of your request history</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;